/*
 * FROSTBEULE – global helpers (owner: CORE). No dependencies, loaded with defer in <head>.
 * window.FROSTBEULE (alias window.FB):
 *   motion()                      -> 'full' | 'soft' | 'none' (html[data-motion])
 *   setMotion(on)                 -> visitor switch: true = animations on, false = off (localStorage)
 *   ready(fn)                     -> run fn once the DOM is parsed
 *   onSectionLoad(fn)             -> fn(scope) on DOM ready (scope = document) and after every shopify:section:load
 *   onSectionUnload(fn)           -> fn(sectionEl) on shopify:section:unload
 *   component(selector, setup)    -> setup(el) once per matching element (ready + section load); setup may
 *                                    return a cleanup function, called on section unload. Returns nothing.
 *   inView(el, cb, opts)          -> IntersectionObserver wrapper, cb(isVisible, entry); returns disconnect()
 *   spring(opts)                  -> tiny rAF spring { set(target), jump(value), stop(), value } (opts: from,
 *                                    stiffness 170, damping 18, mass 1, onUpdate(v), onRest(v))
 *   debounce(fn, wait)            -> debounced function (default 150 ms)
 *   lockScroll() / unlockScroll() -> counted page scroll lock (nested dialogs safe)
 *   getFocusable(el)              -> visible, focusable descendants
 *   focusTrap(el, opts)           -> { release(returnFocus = true) }; opts: initialFocus, onEscape
 *   initReveal(scope) / reveal(el)-> scroll reveal for [data-fb-reveal] (+ [data-fb-stagger] parents)
 *   formatMoney(cents, format)    -> money string in shop format (default FROSTBEULE.config.moneyFormat)
 *   bindOrphans(scope)            -> German typography: number + following word, „z. B.“ etc. glued with &nbsp;
 *   snow                          -> = window.FBSnow (see below)
 *   config, strings               -> printed in layout/theme.liquid
 * window.FBSnow:
 *   mount(el, { density, wind, color, speed, size }) -> instance { update(opts), destroy() }
 *     density 0–100 (default theme setting fb_snow_density), wind −1…1 (default 0.15),
 *     color any CSS colour (default '#fff'), speed / size multipliers (default 1).
 *     Canvas, DPR-aware, max 120 flakes, pauses off-screen / in hidden tabs, soft = fewer + slower,
 *     none = one static frame. Theme setting „Show snowfall“ off → no canvas at all.
 *   unmount(el), mountAll(scope), unmountAll(scope)
 *   Declarative: <div class="fb-snow" data-fb-snow data-density="60" data-wind="0.2" data-color="#fff"
 *   aria-hidden="true"></div> inside a position:relative section – mounted automatically.
 * Automatic: [data-fb-motion-toggle] switches, .fb-offscreen on sections outside the viewport (endless
 * CSS loops pause there), add-to-cart CTAs ([data-fb-cta-form]) load assets/product-form.js on demand.
 */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var C = (window.FROSTBEULE = window.FROSTBEULE || {});
  window.FB = C;
  var each = function (list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  };
  var raf =
    window.requestAnimationFrame ||
    function (fn) {
      return window.setTimeout(function () {
        fn(Date.now());
      }, 16);
    };
  var caf = window.cancelAnimationFrame || window.clearTimeout;

  root.classList.remove('fb-no-js');
  root.classList.add('fb-js');

  /* Motion ---------------------------------------------------------------- */
  C.motion = function () {
    return root.getAttribute('data-motion') || 'full';
  };

  /* Same rules as the inline head script in layout/theme.liquid (keep both in sync):
     session '0' (?motion=0) or stored switch '0' → none; stored switch '1' → never none. */
  var MOTION_KEY = 'fb-motion';

  function storedPreference() {
    try {
      return window.localStorage.getItem(MOTION_KEY);
    } catch (e) {
      return null;
    }
  }

  function modeSetting() {
    return root.getAttribute('data-motion-mode') || 'full';
  }

  function computeMotion(reduced) {
    var off = false;
    try {
      off = window.sessionStorage.getItem(MOTION_KEY) === '0';
    } catch (e) {
      off = /[?&]motion=0(&|#|$)/.test(window.location.search);
    }
    var pref = storedPreference();
    var mode = modeSetting();
    if (off || pref === '0') return 'none';
    if (pref === '1') return mode !== 'full' && reduced ? 'soft' : 'full';
    if (mode === 'full') return 'full';
    if (mode === 'respect') return reduced ? 'none' : 'full';
    return reduced ? 'soft' : 'full';
  }

  var mql = null;
  try {
    mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  } catch (e) {
    /* matchMedia unavailable – keep the value from the head script */
  }

  function applyMotion(next) {
    if (next === C.motion()) return;
    root.setAttribute('data-motion', next);
    if (next === 'none') each(doc.querySelectorAll('[data-fb-reveal]'), reveal);
    syncMotionToggles();
    doc.dispatchEvent(new CustomEvent('frost:motion-change', { detail: { motion: next } }));
  }

  if (mql) {
    var onChange = function (e) {
      applyMotion(computeMotion(e.matches));
    };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
  }

  /* Visitor switch (WCAG 2.2.2 pause/stop/hide): off = 'none' on every visit until switched on again. */
  C.setMotion = function (on) {
    try {
      window.sessionStorage.removeItem(MOTION_KEY);
    } catch (e) {}
    try {
      window.localStorage.setItem(MOTION_KEY, on ? '1' : '0');
    } catch (e) {}
    var reduced = !!(mql && mql.matches);
    applyMotion(on ? (modeSetting() !== 'full' && reduced ? 'soft' : 'full') : 'none');
  };

  function syncMotionToggles() {
    var on = C.motion() !== 'none';
    each(doc.querySelectorAll('[data-fb-motion-toggle]'), function (btn) {
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.hidden = false;
    });
  }

  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-fb-motion-toggle]');
    if (!btn) return;
    e.preventDefault();
    C.setMotion(btn.getAttribute('aria-checked') !== 'true');
  });

  /* Small helpers ----------------------------------------------------------- */
  C.ready = function (fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  C.debounce = function (fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait == null ? 150 : wait);
    };
  };

  C.onSectionLoad = function (fn) {
    C.ready(function () {
      fn(doc);
    });
    doc.addEventListener('shopify:section:load', function (e) {
      fn(e.target);
    });
  };

  C.onSectionUnload = function (fn) {
    doc.addEventListener('shopify:section:unload', function (e) {
      fn(e.target);
    });
  };

  function queryWithin(scope, selector) {
    if (!scope) return [];
    var list = Array.prototype.slice.call(scope.querySelectorAll ? scope.querySelectorAll(selector) : []);
    if (scope.matches && scope.matches(selector)) list.unshift(scope);
    return list;
  }

  var componentId = 0;
  C.component = function (selector, setup) {
    var key = '__fbComponent' + (componentId += 1);
    function init(scope) {
      queryWithin(scope, selector).forEach(function (el) {
        if (el[key]) return;
        var cleanup = null;
        try {
          cleanup = setup(el);
        } catch (err) {
          if (window.console) console.error('[FROSTBEULE] ' + selector, err);
        }
        el[key] = typeof cleanup === 'function' ? cleanup : true;
      });
    }
    function teardown(scope) {
      queryWithin(scope, selector).forEach(function (el) {
        var cleanup = el[key];
        if (typeof cleanup === 'function') {
          try {
            cleanup();
          } catch (err) {}
        }
        el[key] = null;
      });
    }
    C.onSectionLoad(init);
    C.onSectionUnload(teardown);
  };

  C.inView = function (el, cb, opts) {
    opts = opts || {};
    if (!el) return function () {};
    if (!('IntersectionObserver' in window)) {
      cb(true, null);
      return function () {};
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          cb(entry.isIntersecting, entry);
          if (opts.once && entry.isIntersecting) observer.disconnect();
        });
      },
      { rootMargin: opts.rootMargin || '0px', threshold: opts.threshold || 0 }
    );
    observer.observe(el);
    return function () {
      observer.disconnect();
    };
  };

  /* Spring (semi-implicit Euler, fixed 1/120 s sub-steps) – for tilt, drag release, counters. */
  C.spring = function (opts) {
    opts = opts || {};
    var k = opts.stiffness || 170;
    var c = opts.damping || 18;
    var m = opts.mass || 1;
    var precision = opts.precision || 0.001;
    var s = { value: opts.from || 0, velocity: 0, target: opts.from || 0 };
    var frame = null;
    var last = 0;

    function step(now) {
      var dt = Math.min(0.064, (now - last) / 1000 || 0.016);
      last = now;
      var sub = Math.max(1, Math.ceil(dt / (1 / 120)));
      var h = dt / sub;
      for (var i = 0; i < sub; i += 1) {
        var force = -k * (s.value - s.target) - c * s.velocity;
        s.velocity += (force / m) * h;
        s.value += s.velocity * h;
      }
      if (Math.abs(s.velocity) < precision && Math.abs(s.value - s.target) < precision) {
        s.value = s.target;
        s.velocity = 0;
        frame = null;
        if (opts.onUpdate) opts.onUpdate(s.value);
        if (opts.onRest) opts.onRest(s.value);
        return;
      }
      if (opts.onUpdate) opts.onUpdate(s.value);
      frame = raf(step);
    }

    s.set = function (target) {
      s.target = target;
      if (!frame) {
        last = window.performance ? performance.now() : Date.now();
        frame = raf(step);
      }
    };
    s.jump = function (value) {
      s.value = value;
      s.target = value;
      s.velocity = 0;
      if (opts.onUpdate) opts.onUpdate(value);
    };
    s.stop = function () {
      if (frame) caf(frame);
      frame = null;
    };
    return s;
  };

  /* Scroll lock ------------------------------------------------------------------ */
  var lockCount = 0;
  var savedPadding = '';

  C.lockScroll = function () {
    lockCount += 1;
    if (lockCount > 1) return;
    var scrollbar = window.innerWidth - root.clientWidth;
    savedPadding = doc.body.style.paddingRight;
    if (scrollbar > 0) doc.body.style.paddingRight = scrollbar + 'px';
    root.classList.add('fb-scroll-locked');
  };

  C.unlockScroll = function () {
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount > 0) return;
    doc.body.style.paddingRight = savedPadding;
    root.classList.remove('fb-scroll-locked');
  };

  /* Focus trap ---------------------------------------------------------------- */
  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  C.getFocusable = function (el) {
    if (!el) return [];
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      if (node.closest('[inert]')) return false;
      if (node.closest('details:not([open])') && node.tagName !== 'SUMMARY') return false;
      if (!(node.offsetWidth || node.offsetHeight || node.getClientRects().length)) return false;
      return window.getComputedStyle(node).visibility !== 'hidden';
    });
  };

  C.focusTrap = function (el, opts) {
    opts = opts || {};
    var previous = doc.activeElement;
    var released = false;

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (typeof opts.onEscape === 'function') {
          e.preventDefault();
          opts.onEscape(e);
        }
        return;
      }
      if (e.key !== 'Tab') return;
      var nodes = C.getFocusable(el);
      if (!nodes.length) {
        e.preventDefault();
        el.focus({ preventScroll: true });
        return;
      }
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      var active = doc.activeElement;
      if (e.shiftKey && (active === first || active === el || !el.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !el.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }

    function onFocusIn(e) {
      if (el.contains(e.target)) return;
      var nodes = C.getFocusable(el);
      (nodes[0] || el).focus({ preventScroll: true });
    }

    doc.addEventListener('keydown', onKeydown, true);
    doc.addEventListener('focusin', onFocusIn);

    function resolveInitial() {
      var target = opts.initialFocus;
      if (typeof target === 'string') target = el.querySelector(target);
      if (!target) target = C.getFocusable(el)[0];
      if (!target) {
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        target = el;
      }
      return target;
    }

    function focusInitial() {
      var target = resolveInitial();
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
      return doc.activeElement === target;
    }

    /* A dialog that turns visible through a `visibility` transition is still hidden in the frame it opens;
       focus() on a hidden element silently fails. Retry for a few frames. */
    if (!focusInitial()) {
      var attempts = 0;
      var retry = function () {
        raf(function () {
          if (released) return;
          var active = doc.activeElement;
          if (active && active !== el && el.contains(active)) return;
          if (focusInitial() || ++attempts >= 20) return;
          retry();
        });
      };
      retry();
    }

    return {
      release: function (returnFocus) {
        if (released) return;
        released = true;
        doc.removeEventListener('keydown', onKeydown, true);
        doc.removeEventListener('focusin', onFocusIn);
        if (returnFocus !== false && previous && typeof previous.focus === 'function' && doc.contains(previous)) {
          try {
            previous.focus({ preventScroll: true });
          } catch (e) {
            previous.focus();
          }
        }
      },
    };
  };

  /* Money ---------------------------------------------------------------------- */
  C.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || (C.config && C.config.moneyFormat) || '{{amount_with_comma_separator}} €';

    function fmt(number, precision, thousands, decimal) {
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return format
      .replace(/<[^>]*>/g, '')
      .replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
        switch (key) {
          case 'amount':
            return fmt(cents, 2, ',', '.');
          case 'amount_no_decimals':
            return fmt(cents, 0, ',', '.');
          case 'amount_with_comma_separator':
            return fmt(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return fmt(cents, 0, '.', ',');
          case 'amount_with_space_separator':
            return fmt(cents, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return fmt(cents, 0, ' ', ',');
          case 'amount_with_apostrophe_separator':
            return fmt(cents, 2, "'", '.');
          default:
            return fmt(cents, 2, ',', '.');
        }
      });
  };

  /* Reveal ------------------------------------------------------------------------
     [data-fb-reveal="fade|up|scale|left|right"] (empty = up), optional style="--fb-delay:120ms".
     [data-fb-stagger="80"] on a parent gives its revealing children 0, 80, 160 … ms (max 360). */
  var io = null;

  function reveal(el) {
    el.classList.add('is-revealed');
    if (io) io.unobserve(el);
  }
  C.reveal = reveal;

  function applyStagger(scope) {
    queryWithin(scope, '[data-fb-stagger]').forEach(function (parent) {
      var step = parseInt(parent.getAttribute('data-fb-stagger'), 10) || 80;
      var i = 0;
      each(parent.children, function (child) {
        if (!child.hasAttribute('data-fb-reveal')) return;
        if (!child.style.getPropertyValue('--fb-delay')) {
          child.style.setProperty('--fb-delay', Math.min(i * step, 360) + 'ms');
        }
        i += 1;
      });
    });
  }

  C.initReveal = function (scope) {
    scope = scope || doc;
    applyStagger(scope);
    var nodes = queryWithin(scope, '[data-fb-reveal]:not(.is-revealed)');
    if (!nodes.length) return;
    if (C.motion() === 'none' || !('IntersectionObserver' in window)) {
      nodes.forEach(reveal);
      return;
    }
    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) reveal(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0 }
      );
    }
    nodes.forEach(function (el) {
      io.observe(el);
    });
  };

  function revealWithin(target) {
    queryWithin(target, '[data-fb-reveal]').forEach(reveal);
  }

  /* Offscreen pause ------------------------------------------------------------
     Endless decorative CSS loops keep their look but pause while their section is far outside the
     viewport (.fb-offscreen → animation-play-state: paused in frost-base.css). */
  var OFFSCREEN_SELECTOR = '.fb-section, .fb-footer';
  var offscreenIo = null;

  function observeOffscreen(scope) {
    if (!('IntersectionObserver' in window) || !scope) return;
    if (!offscreenIo) {
      offscreenIo = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            entry.target.classList.toggle('fb-offscreen', !entry.isIntersecting);
          });
        },
        { rootMargin: '35% 0px 35% 0px', threshold: 0 }
      );
    }
    queryWithin(scope, OFFSCREEN_SELECTOR).forEach(function (node) {
      offscreenIo.observe(node);
    });
  }

  function unobserveOffscreen(scope) {
    if (!offscreenIo) return;
    queryWithin(scope, OFFSCREEN_SELECTOR).forEach(function (node) {
      offscreenIo.unobserve(node);
    });
  }

  /* German typography ------------------------------------------------------------
     A number stays with the word after it („108 Karten“, „16 Jahren“, „2. November“), short
     abbreviations stay together („z. B.“) and a dash never starts a line („Spiel – fertig“).
     Runs on rendered text nodes; skipped in the theme editor so no &nbsp; can leak into a setting. */
  var NBSP = ' ';
  var TYPO_SCOPES = '.fb-section, .fb-footer, .fb-page-hero';
  var TYPO_SKIP = 'script, style, noscript, template, textarea, input, select, option, pre, code, svg, [contenteditable], [data-fb-no-nbsp]';
  var RE_NUMBER = (function () {
    try {
      return new RegExp('(\\d+\\.?(?:[–-]\\d+\\.?)?)[ \\t\\r\\n\\f]+(?=[\\p{L}€%])', 'gu');
    } catch (e) {
      return /(\d+\.?(?:[–-]\d+\.?)?)[ \t\r\n\f]+(?=[A-Za-zÀ-ž€%])/g;
    }
  })();
  var RE_ABBR = /\b(z|d|u|o|v|s|bzw|ca|inkl|zzgl)\.[ \t\r\n\f]+(?=\S)/g;
  var RE_DASH = /[ \t\r\n\f]+([–—])(?=[ \t\r\n\f])/g;
  /* Short first part of a hyphenated word („E-Mail“, „WG-Abend“): a word joiner (U+2060, invisible, needs no
     glyph) after the hyphen keeps the line from breaking as „E-“ / „Mail“. Longer compounds
     („Winter-Partyspiel“) may still break at their hyphen. */
  var WJ = '⁠';
  var RE_SHORT_COMPOUND = (function () {
    try {
      return new RegExp('(^|[^\\p{L}\\p{N}\\u2060-])(\\p{L}{1,3})-(?=\\p{L})', 'gu');
    } catch (e) {
      return /(^|[^A-Za-zÀ-ž0-9⁠-])([A-Za-zÀ-ž]{1,3})-(?=[A-Za-zÀ-ž])/g;
    }
  })();
  var RE_CANDIDATE = /\d\s|\.\s|\s[–—]\s|\S-\S/;

  function bindText(text) {
    return text
      .replace(RE_NUMBER, '$1' + NBSP)
      .replace(RE_ABBR, '$1.' + NBSP)
      .replace(RE_DASH, NBSP + '$1')
      .replace(RE_SHORT_COMPOUND, '$1$2-' + WJ);
  }

  C.bindOrphans = function (scope) {
    if (C.config && C.config.designMode) return;
    if (!doc.createTreeWalker || !scope) return;
    queryWithin(scope, TYPO_SCOPES).forEach(function (rootEl) {
      var outer = rootEl.parentElement && rootEl.parentElement.closest(TYPO_SCOPES);
      if (outer && (outer === scope || (scope.contains && scope.contains(outer)))) return;
      var walker = doc.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          if (!RE_CANDIDATE.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
          var parent = node.parentElement;
          if (!parent || parent.closest(TYPO_SKIP)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      var node;
      var changes = [];
      while ((node = walker.nextNode())) {
        var next = bindText(node.nodeValue);
        if (next !== node.nodeValue) changes.push([node, next]);
      }
      changes.forEach(function (change) {
        change[0].nodeValue = change[1];
      });
    });
  };

  /* Snowfall --------------------------------------------------------------------- */
  var Snow = (function () {
    var MAX = 120;
    var instances = [];
    var enabled = root.getAttribute('data-fb-snow-enabled') !== 'false';
    var defaultDensity = parseFloat(root.getAttribute('data-fb-snow-density'));
    if (isNaN(defaultDensity)) defaultDensity = 45;

    function num(v, fallback) {
      var n = parseFloat(v);
      return isNaN(n) ? fallback : n;
    }

    function makeSprite(color) {
      var size = 64;
      var c = doc.createElement('canvas');
      c.width = size;
      c.height = size;
      var ctx = c.getContext('2d');
      /* solid dot in any CSS colour, then a radial alpha mask (destination-in) for the soft edge */
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'destination-in';
      var mask = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      mask.addColorStop(0, 'rgba(0,0,0,1)');
      mask.addColorStop(0.5, 'rgba(0,0,0,0.95)');
      mask.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = mask;
      ctx.fillRect(0, 0, size, size);
      return c;
    }

    function Instance(el, opts) {
      this.el = el;
      this.opts = {};
      this.flakes = [];
      this.visible = false;
      this.frame = null;
      this.last = 0;
      this.t = 0;
      this.w = 0;
      this.h = 0;
      var host = el;
      if (!el.classList.contains('fb-snow')) {
        host = doc.createElement('div');
        host.className = 'fb-snow';
        host.setAttribute('aria-hidden', 'true');
        el.appendChild(host);
        this.ownHost = host;
      }
      this.canvas = doc.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      host.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      this.update(opts);

      var self = this;
      this.tick = function (now) {
        self.loop(now);
      };
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(function () {
          self.resize();
        });
        this.ro.observe(host);
      } else {
        this.onResize = C.debounce(function () {
          self.resize();
        }, 200);
        window.addEventListener('resize', this.onResize);
      }
      this.stopView = C.inView(
        host,
        function (visible) {
          self.visible = visible;
          self.sync();
        },
        { rootMargin: '120px 0px' }
      );
      this.resize();
    }

    Instance.prototype.update = function (opts) {
      opts = opts || {};
      var d = this.el.dataset || {};
      var o = this.opts;
      o.density = Math.max(0, Math.min(100, num(opts.density, num(d.density, o.density != null ? o.density : defaultDensity))));
      o.wind = Math.max(-1, Math.min(1, num(opts.wind, num(d.wind, o.wind != null ? o.wind : 0.15))));
      o.speed = Math.max(0.1, num(opts.speed, num(d.speed, o.speed || 1)));
      o.size = Math.max(0.3, num(opts.size, num(d.size, o.size || 1)));
      var color = opts.color || d.color || o.color || '#fff';
      if (color !== o.color || !this.sprite) {
        o.color = color;
        this.sprite = makeSprite(color);
      }
      if (this.w) this.seed();
      this.sync();
      return this;
    };

    Instance.prototype.targetCount = function () {
      var m = C.motion();
      var n = ((this.w * this.h) / 11000) * (this.opts.density / 45);
      if (m === 'soft') n *= 0.5;
      if (m === 'none') n *= 0.5;
      n = Math.round(Math.min(MAX, n));
      if (this.opts.density > 0) n = Math.max(n, 8);
      return n;
    };

    Instance.prototype.makeFlake = function (anywhere) {
      var z = Math.random();
      var bokeh = z > 0.94;
      var r = (bokeh ? 3.6 + Math.random() * 2.6 : 0.7 + z * z * 2.6) * this.opts.size;
      return {
        x: Math.random() * this.w,
        y: anywhere ? Math.random() * this.h : -r * 2 - Math.random() * 40,
        r: r,
        z: z,
        vy: 14 + z * 46 + Math.random() * 8,
        amp: 6 + Math.random() * 18,
        freq: 0.4 + Math.random() * 0.9,
        phase: Math.random() * Math.PI * 2,
        a: bokeh ? 0.22 + Math.random() * 0.15 : 0.35 + z * 0.55,
      };
    };

    Instance.prototype.seed = function () {
      var target = this.targetCount();
      while (this.flakes.length < target) this.flakes.push(this.makeFlake(true));
      if (this.flakes.length > target) this.flakes.length = target;
    };

    Instance.prototype.resize = function () {
      var host = this.canvas.parentNode;
      if (!host) return;
      var rect = host.getBoundingClientRect();
      var w = Math.round(rect.width);
      var h = Math.round(rect.height);
      if (!w || !h) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var scaleX = this.w ? w / this.w : 1;
      var scaleY = this.h ? h / this.h : 1;
      this.w = w;
      this.h = h;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.flakes.forEach(function (f) {
        f.x *= scaleX;
        f.y *= scaleY;
      });
      this.seed();
      this.draw();
    };

    Instance.prototype.draw = function () {
      var ctx = this.ctx;
      ctx.clearRect(0, 0, this.w, this.h);
      var sprite = this.sprite;
      for (var i = 0; i < this.flakes.length; i += 1) {
        var f = this.flakes[i];
        ctx.globalAlpha = f.a;
        ctx.drawImage(sprite, f.x - f.r, f.y - f.r, f.r * 2, f.r * 2);
      }
      ctx.globalAlpha = 1;
    };

    Instance.prototype.step = function (dt) {
      var soft = C.motion() === 'soft';
      var speed = this.opts.speed * (soft ? 0.55 : 1);
      var wind = this.opts.wind;
      this.t += dt;
      for (var i = 0; i < this.flakes.length; i += 1) {
        var f = this.flakes[i];
        var depth = 0.35 + f.z * 0.65;
        f.y += f.vy * speed * dt;
        f.x += (wind * 46 * depth + Math.cos(this.t * f.freq + f.phase) * f.amp * depth * (soft ? 0.4 : 1)) * speed * dt;
        if (f.y - f.r > this.h) {
          var nf = this.makeFlake(false);
          nf.x = Math.random() * this.w;
          this.flakes[i] = nf;
        } else if (f.x - f.r > this.w) {
          f.x = -f.r;
        } else if (f.x + f.r < 0) {
          f.x = this.w + f.r;
        }
      }
    };

    Instance.prototype.loop = function (now) {
      this.frame = null;
      if (!this.running()) return;
      var dt = this.last ? Math.min(0.05, (now - this.last) / 1000) : 0.016;
      this.last = now;
      this.step(dt);
      this.draw();
      this.frame = raf(this.tick);
    };

    Instance.prototype.running = function () {
      return this.visible && !doc.hidden && C.motion() !== 'none' && this.opts.density > 0 && !this.destroyed;
    };

    Instance.prototype.sync = function () {
      if (this.destroyed || !this.tick) return;
      if (this.w) this.seed();
      if (this.running()) {
        if (!this.frame) {
          this.last = 0;
          this.frame = raf(this.tick);
        }
      } else {
        if (this.frame) caf(this.frame);
        this.frame = null;
        if (this.w) this.draw();
      }
    };

    Instance.prototype.destroy = function () {
      this.destroyed = true;
      if (this.frame) caf(this.frame);
      this.frame = null;
      if (this.ro) this.ro.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.stopView) this.stopView();
      if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
      if (this.ownHost && this.ownHost.parentNode) this.ownHost.parentNode.removeChild(this.ownHost);
      var idx = instances.indexOf(this);
      if (idx > -1) instances.splice(idx, 1);
      this.el.__fbSnow = null;
    };

    var noop = {
      update: function () {
        return noop;
      },
      destroy: function () {},
    };

    function mount(el, opts) {
      if (!el || !enabled) return noop;
      if (el.__fbSnow) return el.__fbSnow.update(opts);
      if (!doc.createElement('canvas').getContext) return noop;
      var inst = new Instance(el, opts);
      el.__fbSnow = inst;
      instances.push(inst);
      return inst;
    }

    function unmount(el) {
      if (el && el.__fbSnow) el.__fbSnow.destroy();
    }

    function mountAll(scope) {
      queryWithin(scope || doc, '[data-fb-snow]:not(html)').forEach(function (el) {
        mount(el);
      });
    }

    function unmountAll(scope) {
      instances.slice().forEach(function (inst) {
        if (scope === inst.el || (scope && scope.contains && scope.contains(inst.el))) inst.destroy();
      });
    }

    doc.addEventListener('visibilitychange', function () {
      instances.forEach(function (inst) {
        inst.sync();
      });
    });
    doc.addEventListener('frost:motion-change', function () {
      instances.forEach(function (inst) {
        inst.sync();
      });
    });

    return { mount: mount, unmount: unmount, mountAll: mountAll, unmountAll: unmountAll, instances: instances };
  })();

  window.FBSnow = Snow;
  C.snow = Snow;

  /* Add-to-cart CTAs ----------------------------------------------------------------
     snippets/frost-cta renders <product-form data-fb-cta-form> when its label promises the cart. Dawn's
     product-form.js is loaded once, only on pages that have such a button. Inside an open dialog
     (menu drawer, exit popup) the dialog closes first, so the cart drawer is not hidden behind it. */
  function loadProductForm() {
    if (window.customElements && customElements.get('product-form')) return;
    if (!doc.querySelector('[data-fb-cta-form]')) return;
    if (doc.querySelector('script[src*="product-form.js"]')) return;
    var src = C.config && C.config.productFormScript;
    if (!src) return;
    var script = doc.createElement('script');
    script.src = src;
    script.defer = true;
    doc.head.appendChild(script);
  }

  doc.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.closest || !form.closest('[data-fb-cta-form]')) return;
      var dialog = form.closest('[role="dialog"], dialog');
      if (!dialog) return;
      var closer = dialog.querySelector('[data-fb-drawer-close], [data-fb-exit-close], [data-fb-close]');
      if (closer && typeof closer.click === 'function') closer.click();
    },
    true
  );

  /* Theme editor ------------------------------------------------------------- */
  doc.addEventListener('shopify:section:load', function (e) {
    C.initReveal(e.target);
    C.bindOrphans(e.target);
    observeOffscreen(e.target);
    Snow.mountAll(e.target);
    syncMotionToggles();
    loadProductForm();
  });
  doc.addEventListener('shopify:section:unload', function (e) {
    unobserveOffscreen(e.target);
    Snow.unmountAll(e.target);
    if (!io || !e.target) return;
    each(e.target.querySelectorAll('[data-fb-reveal]'), function (el) {
      io.unobserve(el);
    });
  });
  doc.addEventListener('shopify:section:select', function (e) {
    revealWithin(e.target);
  });
  doc.addEventListener('shopify:block:select', function (e) {
    revealWithin(e.target);
  });

  C.ready(function () {
    C.bindOrphans(doc.body);
    C.initReveal(doc);
    C.revealReady = true;
    observeOffscreen(doc.body);
    Snow.mountAll(doc);
    syncMotionToggles();
    loadProductForm();
  });

  /* revealed content must be visible when printing */
  window.addEventListener('beforeprint', function () {
    each(doc.querySelectorAll('[data-fb-reveal]'), reveal);
  });
})();
